import React, { useState, useEffect } from 'react';
import { Mail, Phone, BookOpen, Plus } from 'lucide-react';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { cn } from '@/lib/utils';
import { useCommunicationTemplates } from '@/hooks/useCommunicationTemplates';
import { useAuth } from '@/context/AuthContext';
import { validateEmailSettings } from '@/services/emailSendingService';
import EmailTemplateView from './EmailTemplateView';
import CallScriptView from './CallScriptView';
import TemplateManager from '@/components/communication/TemplateManager';
import { CommunicationTemplate } from '@/services/communication/templates';

interface CommunicationBottomSheetProps {
  leadId: string;
  isOpen: boolean;
  onClose: () => void;
  defaultTab?: 'email' | 'call';
}

const CommunicationBottomSheet: React.FC<CommunicationBottomSheetProps> = ({
  leadId,
  isOpen,
  onClose,
  defaultTab = 'email'
}) => {
  const { profile } = useAuth();
  const [activeTab, setActiveTab] = useState<'email' | 'call'>(defaultTab);
  const [emailMainTab, setEmailMainTab] = useState<'new' | 'templates'>('new');
  const [callMainTab, setCallMainTab] = useState<'new' | 'templates'>('new');
  const [emailValidation, setEmailValidation] = useState<any>(null);

  const {
    lead,
    emailTemplate,
    callScript,
    isLoadingEmail,
    isLoadingCall,
    handleGenerateEmailTemplate,
    handleGenerateEmailWithPrompt,
    handleGenerateCallScript,
    handleGenerateCallScriptWithPrompt,
    saveCurrentEmailTemplate,
    saveCurrentCallScript
  } = useCommunicationTemplates(leadId);

  useEffect(() => {
    if (isOpen && defaultTab) {
      setActiveTab(defaultTab);
    }
  }, [isOpen, defaultTab]);

  useEffect(() => {
    if (profile?.company_id) {
      checkEmailSettings();
    }
  }, [profile?.company_id]);

  const checkEmailSettings = async () => {
    if (!profile?.company_id) return;
    try {
      const validation = await validateEmailSettings(profile.company_id);
      setEmailValidation(validation);
    } catch (error) {
      console.error('Error checking email settings:', error);
    }
  };

  const handleSelectTemplate = (template: CommunicationTemplate) => {
    if (template.type === 'email') {
      saveCurrentEmailTemplate(template.content);
      setEmailMainTab('new');
    } else {
      saveCurrentCallScript(template.content);
      setCallMainTab('new');
    }
  };

  return (
    <Drawer open={isOpen} onOpenChange={onClose}>
      <DrawerContent className="max-h-[85vh] px-4">
        <DrawerHeader className="pb-4">
          <div className="flex items-center justify-between mb-3">
            <DrawerTitle className="text-xl font-semibold">Communication</DrawerTitle>
          </div>
          
          {/* Mode toggle for mobile */}
          {activeTab && (
            <div className="flex items-center justify-between gap-2 p-2 bg-muted/30 rounded-lg">
              <span className="text-sm font-medium text-muted-foreground flex-1">
                {activeTab === 'email' 
                  ? (emailMainTab === 'new' ? 'Create Email' : 'Email Library')
                  : (callMainTab === 'new' ? 'Create Script' : 'Script Library')
                }
              </span>
              <button
                onClick={() => {
                  if (activeTab === 'email') {
                    setEmailMainTab(emailMainTab === 'new' ? 'templates' : 'new');
                  } else {
                    setCallMainTab(callMainTab === 'new' ? 'templates' : 'new');
                  }
                }}
                className="h-7 px-2 gap-1.5 flex items-center rounded-md hover:bg-muted transition-colors"
              >
                {activeTab === 'email' 
                  ? (emailMainTab === 'new' ? <BookOpen className="h-4 w-4" /> : <Plus className="h-4 w-4" />)
                  : (callMainTab === 'new' ? <BookOpen className="h-4 w-4" /> : <Plus className="h-4 w-4" />)
                }
                <span className="text-xs">
                  {activeTab === 'email'
                    ? (emailMainTab === 'new' ? 'View Saved' : 'Create New')
                    : (callMainTab === 'new' ? 'View Saved' : 'Create New')
                  }
                </span>
              </button>
            </div>
          )}
        </DrawerHeader>
        <div className="overflow-y-auto pb-safe">
          <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'email' | 'call')}>
            <TabsList className="grid w-full grid-cols-2 mb-4">
              <TabsTrigger value="email" className="text-sm gap-2">
                <Mail className="h-4 w-4" />
                Email
              </TabsTrigger>
              <TabsTrigger value="call" className="text-sm gap-2">
                <Phone className="h-4 w-4" />
                Call
              </TabsTrigger>
            </TabsList>

            <TabsContent value="email" className="space-y-4 mt-0">
              {emailMainTab === 'new' ? (
                <EmailTemplateView
                  emailTemplate={emailTemplate}
                  isLoading={isLoadingEmail}
                  onGenerate={handleGenerateEmailTemplate}
                  onGenerateWithPrompt={handleGenerateEmailWithPrompt}
                  leadStatus={lead?.status}
                  lead={lead}
                  emailValidation={emailValidation}
                />
              ) : (
                <TemplateManager
                  type="email"
                  onSelectTemplate={handleSelectTemplate}
                  leadId={leadId}
                />
              )}
            </TabsContent>

            <TabsContent value="call" className="space-y-4 mt-0">
              {callMainTab === 'new' ? (
                <CallScriptView
                  callScript={callScript}
                  isLoading={isLoadingCall}
                  onGenerate={handleGenerateCallScript}
                  onGenerateWithPrompt={handleGenerateCallScriptWithPrompt}
                  leadStatus={lead?.status}
                />
              ) : (
                <TemplateManager
                  type="call"
                  onSelectTemplate={handleSelectTemplate}
                  leadId={leadId}
                />
              )}
            </TabsContent>
          </Tabs>
        </div>
      </DrawerContent>
    </Drawer>
  );
};

export default CommunicationBottomSheet;
